import { PreviewMessageType } from '@webcontainer/api';
import { errorHandler } from '~/lib/error-handling';
import type { SupabaseFunctionError } from '~/lib/preview-scripts';
import { setDevicePreviewURL } from '~/lib/stores/device-preview';
import { supabaseStore } from '~/lib/stores/supabase';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer as webcontainerPromise } from '~/lib/webcontainer';
import { errorTypes, type PreviewErrorMessage } from '~/types/chat';
import { logger } from '~/utils/logger';

export async function subscribeWebContainerEvents() {
  const webcontainer = await webcontainerPromise;

  webcontainer.on('xdg-open', (text) => {
    if (text.startsWith('qr://')) {
      const link = decodeURIComponent(text.slice(5));

      // expo
      if (link.startsWith('exp:')) {
        setDevicePreviewURL(link);
      }
    } else if (/^https?:\/\//.test(text)) {
      if (!/^https?:\/\/localhost/.test(text)) {
        window.open(text, '_blank', 'noopener,noreferrer');
      } else {
        logger.debug('xdg-open: (silenced)', text);
      }
    } else if (/^\w+:/.test(text)) {
      logger.warn(`xdg-open: unsupported URL scheme ${text}`);
    } else {
      openFile(text);
    }
  });

  webcontainer.on('code', (type, event) => {
    if (type === 'open' && event.files.length > 0) {
      const file = event.files.at(-1)!;
      openFile(file.filepath);
    }
  });

  webcontainer.on('preview-message', (error: PreviewErrorMessage) => {
    if (errorTypes.includes(error.type) && !workbenchStore.isProcessing.get()) {
      error.stack = error.stack?.replace(/https:\/\/.*?webcontainer-api\.io/g, '');

      if (error.type === PreviewMessageType.ConsoleError) {
        // verify if its a supabase function error which is true if the args contains a functionSlug
        if (error.args.length > 1 && isSupabaseFunctionError(error.args[1])) {
          const { requestUrl, stack, response } = error.args[1];

          const functionSlug = new URL(requestUrl).pathname.split('/').pop();

          if (functionSlug) {
            supabaseStore.getEdgeFunctionsLogs(functionSlug).then((logs) => {
              errorHandler.push({
                type: 'supabase-function',
                message: `Calling Supabase Edge Function failed:\n${response}\n\nStack:\n${stack}`,
                requestUrl,
                functionSlug,
                logs,
              });
            });

            return;
          }
        }
      }

      errorHandler.push({ type: 'preview', error });
    }
  });
}

function openFile(absPath: string) {
  workbenchStore.setSelectedFile(absPath);
}

function isSupabaseFunctionError(arg: unknown): arg is SupabaseFunctionError {
  return !!arg && typeof arg === 'object' && 'subType' in arg && arg.subType === 'supabase-function';
}
